'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { isArchivedLifecycle } from '@terra/ui-design-system';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import { useSignalTestingScopeEligibility } from '../../../hooks/useSignalTestingScopeEligibility';
import { useSignalAttackRun } from '../../../hooks/useSignalAttackRun';
import { useExecutionsStore } from '../../../store/executionsStore';
import {
  SIGNAL_NEO_STATUS,
  isSignalNeoBusy,
  resolveSignalNeoStatus,
  type SignalNeoStatus,
} from './signal-neo-status.model';
import { useSignalCopilotAttack } from './useSignalCopilotAttack';

export function useSignalRowActions(signal: GroupedSignal) {
  const tBulk = useTranslations('signals.bulkActions');
  const tNeo = useTranslations('proxy.signals.neo');
  const tLifecycle = useTranslations('designSystem.lifecycle');
  const isArchived = isArchivedLifecycle(signal.lifecycle);
  const archivedTooltip = tLifecycle('archivedSignalActionTooltip');
  const { isEligible } = useSignalTestingScopeEligibility({ signal });
  const { canStartSignalAttack, startSignalAttack, isSignalAttackRunning } =
    useSignalAttackRun();
  const neoAttackState = useExecutionsStore((state) =>
    state.getNeoAttackState(signal.id),
  );
  const { attackWithCopilot } = useSignalCopilotAttack();
  const neoStatus = resolveSignalNeoStatus({
    attackStatus: neoAttackState?.status,
    isAttackRequestRunning: isSignalAttackRunning(signal.id),
  });
  const neoStatusLabels: Record<SignalNeoStatus, string> = {
    [SIGNAL_NEO_STATUS.IDLE]: tBulk('sendToNeo'),
    [SIGNAL_NEO_STATUS.QUEUED]: tBulk('neoQueuedTitle'),
    [SIGNAL_NEO_STATUS.RUNNING]: tNeo('running'),
    [SIGNAL_NEO_STATUS.COMPLETED]: tNeo('completed'),
  };
  const neoTooltip = isArchived
    ? archivedTooltip
    : neoStatus === SIGNAL_NEO_STATUS.IDLE && !isEligible
      ? tBulk('neoOutOfTestingScopeSingleTooltip')
      : neoStatusLabels[neoStatus];
  const startNeo = useCallback(() => {
    void startSignalAttack({ signalId: signal.id, isEligible });
  }, [isEligible, signal.id, startSignalAttack]);
  const sendToCopilot = useCallback(() => {
    if (!isArchived) {
      attackWithCopilot([signal]);
    }
  }, [attackWithCopilot, isArchived, signal]);

  return {
    isArchived,
    archivedTooltip,
    neoStatus,
    neoThreadId: neoAttackState?.threadId,
    neoTooltip,
    isNeoDisabled:
      isArchived ||
      isSignalNeoBusy(neoStatus) ||
      !isEligible ||
      !canStartSignalAttack,
    startNeo,
    sendToCopilot,
    copilotLabel: tBulk('sendToCopilot'),
    neoLabel: tBulk('sendToNeo'),
  };
}

export type SignalRowActions = ReturnType<typeof useSignalRowActions>;
